import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import type { AnyPgColumn } from 'drizzle-orm/pg-core';
import { claims } from './claims';
import { documents } from './documents';
import { contactTypeEnum, contactEntityTypeEnum, extractionStatusEnum } from './enums';
import { organizations } from './organizations';
import { users } from './users';

// Contacts table
export const contacts = pgTable("contacts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  email: varchar("email"),
  phone: varchar("phone"),
  mobilePhone: varchar("mobile_phone"),
  officePhone: varchar("office_phone"),
  telephone: varchar("telephone"),
  deskExtension: varchar("desk_extension"),
  contactType: contactTypeEnum("contact_type").notNull(),
  entityType: contactEntityTypeEnum("entity_type").notNull().default("individual"),
  companyId: uuid("company_id").references((): AnyPgColumn => contacts.id, { onDelete: "set null" }),
  address: text("address"),
  city: varchar("city"),
  state: varchar("state"),
  zip: varchar("zip"),
  notes: text("notes"),
  
  // Manual Intervention Tracking
  needsManualIntervention: boolean("needs_manual_intervention").default(false),
  interventionReason: text("intervention_reason"), // "no_email", "not_tech_savvy", "both", "other"
  isTechSavvy: boolean("is_tech_savvy").default(true),
  interventionFlaggedAt: timestamp("intervention_flagged_at"),
  interventionFlaggedBy: varchar("intervention_flagged_by").references(() => users.id),
  interventionResolvedAt: timestamp("intervention_resolved_at"),
  interventionResolvedBy: varchar("intervention_resolved_by").references(() => users.id),
  interventionNotes: text("intervention_notes"),
  
  // Email Deduplication Tracking
  lastSeenInEmail: timestamp("last_seen_in_email"), // Last time this contact appeared in an email
  emailOccurrenceCount: integer("email_occurrence_count").default(0), // Number of times seen in emails

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_contacts_company_id").on(table.companyId),
]);


// Extracted contacts from documents/emails awaiting review
export const extractedContacts = pgTable("extracted_contacts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  sourceDocumentId: uuid("source_document_id").references(() => documents.id, { onDelete: "cascade" }),
  
  // Extraction source
  extractionSource: varchar("extraction_source").notNull(), // 'document_ocr', 'email_header', 'email_signature', 'email_body'
  
  // Extracted contact data
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  email: varchar("email"),
  phone: varchar("phone"),
  company: varchar("company"),
  title: varchar("title"),
  
  // Extraction metadata
  extractionRawData: jsonb("extraction_raw_data"), // Original extraction data with full AI response
  evidenceText: text("evidence_text"), // Text snippet showing where contact was found
  confidence: integer("confidence").default(50), // AI confidence score 0-100
  suggestedContactType: varchar("suggested_contact_type"), // AI-suggested contact type
  
  // Matching and linking
  matchedExistingContactId: uuid("matched_existing_contact_id").references(() => contacts.id), // If matched to existing contact
  extractionStatus: extractionStatusEnum("extraction_status").notNull().default("pending"),
  
  // Link tracking (when user approves)
  linkedContactId: uuid("linked_contact_id").references(() => contacts.id), // Contact this was linked to
  linkedBy: varchar("linked_by").references(() => users.id),
  linkedAt: timestamp("linked_at"),
  
  // Dismiss tracking (when user rejects)
  dismissedBy: varchar("dismissed_by").references(() => users.id),
  dismissedAt: timestamp("dismissed_at"),
  
  // Review notes
  reviewNotes: text("review_notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_extracted_contacts_claim_id").on(table.claimId),
  index("idx_extracted_contacts_source_document_id").on(table.sourceDocumentId),
  index("idx_extracted_contacts_extraction_status").on(table.extractionStatus),
  index("idx_extracted_contacts_linked_contact_id").on(table.linkedContactId),
  index("idx_extracted_contacts_matched_existing_contact_id").on(table.matchedExistingContactId),
]);


// Extracted deadlines table for AI-extracted deadlines from documents
export const extractedDeadlines = pgTable("extracted_deadlines", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  sourceDocumentId: uuid("source_document_id").references(() => documents.id, { onDelete: "cascade" }),
  
  // Extracted deadline data
  deadlineType: varchar("deadline_type").notNull(), // 'proof_of_loss_submitted', 'carrier_response', 'depreciation_recovery', 'statute_limitations', 'appraisal_deadline', 'other'
  deadlineDate: timestamp("deadline_date").notNull(),
  description: text("description"), // Description of what this deadline is for
  
  // Extraction metadata
  extractionRawData: jsonb("extraction_raw_data"), // Original extraction data with full AI response
  evidenceText: text("evidence_text"), // Text snippet showing where deadline was found
  confidence: integer("confidence").default(50), // AI confidence score 0-100
  
  // Review and application status
  extractionStatus: extractionStatusEnum("extraction_status").notNull().default("pending"), // pending, approved, dismissed
  
  // Application tracking (when user approves)
  appliedToClaimAt: timestamp("applied_to_claim_at"),
  appliedBy: varchar("applied_by").references(() => users.id),
  
  // Dismiss tracking (when user rejects)
  dismissedBy: varchar("dismissed_by").references(() => users.id),
  dismissedAt: timestamp("dismissed_at"),
  
  // Review notes
  reviewNotes: text("review_notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_extracted_deadlines_claim_id").on(table.claimId),
  index("idx_extracted_deadlines_source_document_id").on(table.sourceDocumentId),
  index("idx_extracted_deadlines_extraction_status").on(table.extractionStatus),
]);
